import { all, call, select, takeEvery } from 'redux-saga/effects';
import { fetchEntity, fetchEntityIfNeeded } from '@classpass/saga-tools';
import { selectRouteParams, selectActiveVenueId } from 'selectors/general';

import URLS from 'constants/urls';

import {
  statsReportsReservations as statsReportsReservationsEntity,
  statsReportsRevenue as statsReportsRevenueEntity,
  statsInsightsModule as statsInsightsModuleEntity,
  statsMarketInsightsModulesList as statsMarketInsightsModulesListEntity,
  statsYourInsightsModulesList as statsYourInsightsModulesListEntity,
  payoutReports as payoutReportsEntity,
  payoutInvoiceStatus as payoutInvoiceStatusEntity,
  payoutDetails as payoutDetailsEntity,
} from 'store/saga/entities/bound';
import { ACTIVE_VENUE_CHANGED } from 'features/App';
import braze, { BRAZE_CUSTOM_EVENTS } from 'helpers/braze';
import { reportError } from 'helpers/error-logging';
import { makePath } from 'helpers/link-generation';
import Segment from 'helpers/segment';

import { constructParamsCsv } from './Stats.helpers';
import { CHANGE_PAYOUTS_PAGE, SET_RANGE } from './Stats.reducers';
import { selectRange } from './TabInsightsShared/InsightsTab.selectors';
import { PAYOUTS_PER_PAGE, STATS_TAB_TYPES } from './Stats.constants';

export const SET_TAB = 'stats/SET_TAB';
export const DOWNLOAD_RESERVATIONS_CSV = 'stats/reports/DOWNLOAD_RESERVATIONS_CSV';
export const GET_PAYOUTS = 'stats/GET_PAYOUTS';
export const GET_PAYOUT_DETAILS = 'stats/GET_PAYOUT_DETAILS';

export const setTab = tab => ({
  type: SET_TAB,
  tab,
});

export const downloadReservationsCSV = (values, formAsyncFns) => ({
  type: DOWNLOAD_RESERVATIONS_CSV,
  ...values,
  ...formAsyncFns,
});

export const getPayouts = (venueId, payoutsPage = 1) => ({
  type: GET_PAYOUTS,
  venueId,
  payoutsPage,
});

export const getPayoutDetails = payoutId => ({
  type: GET_PAYOUT_DETAILS,
  payoutId,
});

export function* doDownloadReservationsCSV(dependencies, action) {
  const { httpClient } = dependencies;
  const { data, filters, isReservationsPage, resolveSubmit } = action;
  const bodyParams = constructParamsCsv(isReservationsPage, filters, data.classIds);
  const endpoint = URLS.reports.downloadReservationsCSV;

  Segment.event('Reservation Report Downloaded', {
    venueId: filters.selectedVenues,
    page: isReservationsPage ? 'bookings' : 'classes',
  });

  const res = yield call(httpClient.post, endpoint, bodyParams);

  // this little song and dance with the Blobs and the manual dom manipulation seems
  // to be the only way to "automatically" download data off a POST request; if you,
  // future reader, know a better one, feel free to improve it

  // Extract desired filename from content-disposition header
  const fileName = res.headers['content-disposition'].split('filename=')[1];
  // Blob of the actual data to download
  const blob = new Blob([res.data]);

  // JSDOM does not like all this BS, so gating it for tests
  if (process.env.NODE_ENV !== 'test') {
    // IE & older versions of Edge don't support the "download" attribute on links but do support the non-standard navigator.msSaveOrOpenBlob
    if (window.navigator.msSaveOrOpenBlob) {
      window.navigator.msSaveOrOpenBlob(blob, fileName);
    } else {
      // Create link to download blob
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      // Append link to page
      document.body.appendChild(link);
      // Click at link to force download
      link.click();
      // Cleanup gnar manual dom manipulation for link
      if (link?.parentNode?.removeChild) link.parentNode.removeChild(link);
    }
  }

  yield all([call(resolveSubmit), call(braze.trackEvent, BRAZE_CUSTOM_EVENTS.RESERVATION_REPORT_DOWNLOADED)]);
}

export function* handleChangeField(dependencies, action) {
  let { venueId, range, tab } = action;

  if (!range) {
    range = yield select(selectRange);
  }

  if (!venueId) {
    venueId = yield select(selectActiveVenueId);
  }

  if (!tab) {
    ({ tab } = yield select(selectRouteParams));
  }

  yield call(
    dependencies.navigate,
    tab === 'reports' ? makePath.reports() : makePath.stats(tab, { venueId }, { range })
  );
}

export function* handleMarketInsightsRouteEnter(dependencies) {
  const venueId = yield select(selectActiveVenueId);
  const range = yield select(selectRange);
  const statsMarketInsightsModulesList = yield call(fetchEntityIfNeeded, statsMarketInsightsModulesListEntity, [
    dependencies,
    venueId,
  ]);
  yield all(
    statsMarketInsightsModulesList.modules.map(({ url }) =>
      call(fetchEntityIfNeeded, statsInsightsModuleEntity, [dependencies, url, range])
    )
  );
}

export function* handleRouteEnter(dependencies) {
  const params = yield select(selectRouteParams);
  const { tab } = params;
  let { venueId } = params;

  if (!venueId) {
    venueId = yield select(selectActiveVenueId);
    if (tab !== STATS_TAB_TYPES.REPORTS) {
      return yield call(dependencies.navigate, makePath.stats(tab || STATS_TAB_TYPES.YOUR_INSIGHTS, { venueId }));
    }
  }

  if (tab === STATS_TAB_TYPES.MARKET_INSIGHTS) {
    return yield call(handleMarketInsightsRouteEnter, dependencies);
  }

  const range = yield select(selectRange);
  const [statsYourInsightsModulesList] = yield all([
    call(fetchEntityIfNeeded, statsYourInsightsModulesListEntity, [dependencies, venueId]),
    call(fetchEntityIfNeeded, statsReportsReservationsEntity, [dependencies]),
    call(fetchEntityIfNeeded, statsReportsRevenueEntity, [dependencies]),
  ]);
  const effects = [];
  statsYourInsightsModulesList.map(({ url }) =>
    effects.push(call(fetchEntityIfNeeded, statsInsightsModuleEntity, [dependencies, url, range]))
  );
  yield all(effects);
  yield call(braze.trackEvent, BRAZE_CUSTOM_EVENTS.STATS_SECTION_VIEWED);
}

export function* handleGetPayouts(dependencies, { venueId, payoutsPage }) {
  const activeVenueId = yield venueId || select(selectActiveVenueId);
  yield call(fetchEntity, payoutReportsEntity, [dependencies, activeVenueId, payoutsPage - 1, PAYOUTS_PER_PAGE]);
}

export function* handleGetPayoutDetails(dependencies, { payoutId }) {
  const venueId = yield select(selectActiveVenueId);
  try {
    yield all([
      call(fetchEntity, payoutInvoiceStatusEntity, [dependencies, payoutId, venueId]),
      call(fetchEntity, payoutDetailsEntity, [dependencies, { payoutId, venueId }]),
    ]);
  } catch (error) {
    reportError('Error in getPayoutDetails:', error);
  }
}

export function* watchActions(dependencies) {
  yield takeEvery(SET_TAB, handleChangeField, dependencies);
  yield takeEvery(ACTIVE_VENUE_CHANGED, handleChangeField, dependencies);
  yield takeEvery(SET_RANGE, handleChangeField, dependencies);
  yield takeEvery(GET_PAYOUTS, handleGetPayouts, dependencies);
  yield takeEvery(CHANGE_PAYOUTS_PAGE, handleGetPayouts, dependencies);
  yield takeEvery(GET_PAYOUT_DETAILS, handleGetPayoutDetails, dependencies);
}

export function* watchCSVActions(dependencies) {
  yield takeEvery(DOWNLOAD_RESERVATIONS_CSV, doDownloadReservationsCSV, dependencies);
}
